import { useCallback, useEffect, useState } from "react";

import type { EhrAdapter } from "./EhrAdapter";
import type { EhrPatientContext } from "./elationPatientToContext";
import type { EhrMemberMatch } from "../graphql/resolveEhrMember";
import { deriveResolutionState, type ResolutionState } from "./resolutionState";

export function useResolvedMember(
  ctx: EhrPatientContext | null,
  adapter: EhrAdapter,
  options?: { includeInactive?: boolean },
): { state: ResolutionState; retry: () => void } {
  const ehrPatientId = ctx?.ehrPatientId;
  const includeInactive = options?.includeInactive;
  const [retryNonce, setRetryNonce] = useState(0);
  const [matches, setMatches] = useState<EhrMemberMatch[]>([]);
  const [resolvedForId, setResolvedForId] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const retry = useCallback(() => setRetryNonce((n) => n + 1), []);

  useEffect(() => {
    // No id => nothing to resolve via crosswalk (manual demographic search handles this).
    if (!ehrPatientId || !ctx) {
      setMatches([]);
      setResolvedForId(undefined);
      setError(null);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setMatches([]);
    setResolvedForId(undefined);
    setError(null);
    setLoading(true);
    // Prefer ACTIVE enrollments. Only fall back to including inactive/termed
    // enrollments when the active-only resolve finds nobody — unless the caller
    // (the card's toggle) explicitly opted into inactive up front. This gives a
    // termed patient a match without defaulting the whole flow to inactive.
    const resolveWithFallback = async (): Promise<EhrMemberMatch[]> => {
      if (includeInactive) {
        return adapter.resolveMembers(ctx, { includeInactive: true });
      }
      const active = await adapter.resolveMembers(ctx, { includeInactive: false });
      if (active.length > 0) return active;
      return adapter.resolveMembers(ctx, { includeInactive: true });
    };
    resolveWithFallback()
      .then((result) => {
        if (cancelled) return;
        setMatches(result);
        setResolvedForId(ehrPatientId);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        // A crosswalk resolve failure must never surface in the UI. The common
        // case today is the backend `EhrSourceSystem` enum not yet accepting a
        // newer adapter's id (e.g. EMR_ECW) — a GraphQL validation error that
        // is expected until the backend catches up. Swallow it and present as
        // "no crosswalk match" so the flow falls through to the manual
        // demographic search (resolution.kind === "none"). Logged for
        // diagnostics only.
        console.warn(
          "[ehr] resolveEhrMember failed; falling back to demographic search:",
          err,
        );
        setMatches([]);
        setResolvedForId(ehrPatientId);
        setError(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // ctx identity changes whenever the scrape refreshes; we only re-run on id change.
    // retryNonce triggers a re-run when the user requests a retry.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ehrPatientId, adapter, retryNonce, includeInactive]);

  return { state: deriveResolutionState({ ehrPatientId, resolvedForId, loading, error, matches }), retry };
}
